import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const AnalyticsTracker = () => {
    const location = useLocation();

    useEffect(() => {
        // Ждем загрузки gtag
        const waitForGtag = () => {
            return new Promise((resolve) => {
                if (window.gtag) {
                    resolve();
                } else {
                    setTimeout(() => waitForGtag().then(resolve), 100);
                }
            });
        };

        const trackAnalytics = async () => {
            await waitForGtag();

            // Парсим URL параметры
            const urlParams = new URLSearchParams(location.search);

            // UTM параметры
            const utmSource = urlParams.get('utm_source');
            const utmMedium = urlParams.get('utm_medium');
            const utmCampaign = urlParams.get('utm_campaign');
            const utmContent = urlParams.get('utm_content');
            const utmTerm = urlParams.get('utm_term');

            // Email параметры
            const emailId = urlParams.get('email_id');
            const leadId = urlParams.get('lead_id');
            const userId = urlParams.get('user_id');

            // Обрабатываем UTM параметры
            if (utmSource || utmMedium || utmCampaign) {
                const utmData = {
                    utm_source: utmSource,
                    utm_medium: utmMedium,
                    utm_campaign: utmCampaign,
                    utm_content: utmContent,
                    utm_term: utmTerm,
                    timestamp: new Date().toISOString(),
                    landing_page: location.pathname,
                    session_id: generateSessionId()
                };

                // Сохраняем UTM данные
                localStorage.setItem('utm_data', JSON.stringify(utmData));

                // Правильная отправка UTM событий в GA4
                if (window.gtag) {
                    // Стандартный page_view с UTM параметрами
                    window.gtag('config', 'G-J8PS02EMPQ', {
                        campaign_source: utmSource,
                        campaign_medium: utmMedium,
                        campaign_name: utmCampaign,
                        campaign_content: utmContent,
                        campaign_term: utmTerm,
                        page_location: window.location.href,
                        page_path: location.pathname
                    });

                    // Кастомное событие для UTM landing
                    window.gtag('event', 'utm_landing', {
                        event_category: 'marketing',
                        event_label: utmCampaign || 'unknown',
                        // Стандартные параметры GA4 для кампаний
                        campaign_source: utmSource,
                        campaign_medium: utmMedium,
                        campaign_name: utmCampaign,
                        campaign_content: utmContent,
                        campaign_term: utmTerm,
                        // Кастомные параметры (нужно настроить в GA4)
                        custom_campaign_source: utmSource,
                        custom_campaign_medium: utmMedium,
                        custom_campaign_name: utmCampaign
                    });
                }

                // GTM остается без изменений
                if (window.dataLayer) {
                    window.dataLayer.push({
                        event: 'utm_landing',
                        utm_source: utmSource,
                        utm_medium: utmMedium,
                        utm_campaign: utmCampaign,
                        utm_content: utmContent,
                        utm_term: utmTerm,
                        page_path: location.pathname
                    });
                }
            }

            // Обработка email параметров
            if (emailId || leadId) {
                const emailTrackingData = {
                    email_id: emailId,
                    lead_id: leadId,
                    user_id: userId,
                    timestamp: new Date().toISOString(),
                    landing_page: location.pathname,
                    referrer: document.referrer,
                    user_agent: navigator.userAgent
                };

                localStorage.setItem('email_tracking', JSON.stringify(emailTrackingData));

                // Отправляем в GA4 с правильными параметрами
                if (window.gtag) {
                    window.gtag('event', 'email_click', {
                        event_category: 'email_marketing',
                        event_label: `lead_${leadId}`,
                        // Кастомные параметры для email трекинга
                        email_id: emailId,
                        lead_id: leadId,
                        user_id: userId,
                        campaign_source: utmSource || 'email',
                        campaign_name: utmCampaign || 'unknown',
                        // Дополнительные параметры
                        email_campaign: utmCampaign,
                        email_source: 'email_link'
                    });

                    // Обновляем конфигурацию с email контекстом
                    window.gtag('config', 'G-J8PS02EMPQ', {
                        // Используем стандартные параметры вместо custom_map
                        user_id: userId,
                        campaign_source: utmSource || 'email',
                        campaign_name: utmCampaign || 'email_campaign',
                        // Кастомные параметры (настройте в GA4 интерфейсе)
                        custom_email_id: emailId,
                        custom_lead_id: leadId
                    });
                }

                // GTM для email трекинга
                if (window.dataLayer) {
                    window.dataLayer.push({
                        event: 'email_user_landing',
                        email_id: emailId,
                        lead_id: leadId,
                        user_id: userId,
                        utm_campaign: utmCampaign,
                        page_path: location.pathname,
                        timestamp: new Date().toISOString()
                    });
                }

                // Отправляем клик в backend
                sendEmailClickToBackend(emailId, window.location.href);
            }

            // Стандартное отслеживание страниц
            const savedUtmData = JSON.parse(localStorage.getItem('utm_data') || '{}');
            const savedEmailData = JSON.parse(localStorage.getItem('email_tracking') || '{}');

            // Отправляем page_view с сохраненным контекстом
            if (window.gtag) {
                window.gtag('event', 'page_view', {
                    page_title: document.title,
                    page_location: window.location.href,
                    page_path: location.pathname,
                    // Добавляем сохраненные UTM данные
                    campaign_source: savedUtmData.utm_source,
                    campaign_medium: savedUtmData.utm_medium,
                    campaign_name: savedUtmData.utm_campaign,
                    // Email контекст
                    user_type: savedEmailData.email_id ? 'email_user' : 'organic',
                    email_context: savedEmailData.email_id ? 'has_email_id' : 'no_email_id',
                    custom_lead_id: savedEmailData.lead_id
                });
            }

            // Трекинг времени на странице для email пользователей
            if (emailId || leadId || savedEmailData.email_id) {
                setupTimeTracking(emailId || savedEmailData.email_id, leadId || savedEmailData.lead_id);
            }
        };

        trackAnalytics();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location]);

    // Функция для отслеживания времени на странице
    const setupTimeTracking = (emailId, leadId) => {
        const pageStartTime = Date.now();

        const trackTimeOnPage = () => {
            const timeSpent = Math.round((Date.now() - pageStartTime) / 1000);

            if (window.gtag && timeSpent > 10) {
                window.gtag('event', 'email_user_engagement', {
                    event_category: 'email_marketing',
                    event_label: 'time_on_page',
                    value: timeSpent,
                    email_id: emailId,
                    lead_id: leadId,
                    page_path: location.pathname,
                    engagement_time: timeSpent
                });
            }
        };

        const handleBeforeUnload = () => trackTimeOnPage();
        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
            trackTimeOnPage();
        };
    };

    // Генерация session ID
    const generateSessionId = () => {
        let sessionId = sessionStorage.getItem('session_id');
        if (!sessionId) {
            sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
            sessionStorage.setItem('session_id', sessionId);
        }
        return sessionId;
    };

    // Отправка в backend
    const sendEmailClickToBackend = async (emailId, clickUrl) => {
        if (!emailId) return;

        try {
            await fetch('/api/devtools/email-tracking/', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    email_id: emailId,
                    url: clickUrl,
                    timestamp: new Date().toISOString()
                })
            });
        } catch (error) {
            console.error('Failed to track email click:', error);
        }
    };

    return null;
};

// Обновленные утилитарные функции
export const trackEmailEvent = (eventName, eventData = {}) => {
    const emailData = JSON.parse(localStorage.getItem('email_tracking') || '{}');
    const utmData = JSON.parse(localStorage.getItem('utm_data') || '{}');

    if (window.gtag) {
        window.gtag('event', eventName, {
            event_category: 'email_marketing',
            email_id: emailData.email_id,
            lead_id: emailData.lead_id,
            campaign_source: utmData.utm_source || 'email',
            campaign_name: utmData.utm_campaign,
            ...eventData
        });
    }

    if (window.dataLayer) {
        window.dataLayer.push({
            event: eventName,
            email_id: emailData.email_id,
            lead_id: emailData.lead_id,
            utm_campaign: utmData.utm_campaign,
            ...eventData
        });
    }
};

// Функция для трекинга конверсий
export const trackEmailConversion = (conversionType, conversionValue = null) => {
    const emailData = JSON.parse(localStorage.getItem('email_tracking') || '{}');
    const utmData = JSON.parse(localStorage.getItem('utm_data') || '{}');

    if (window.gtag) {
        // Основное событие конверсии
        window.gtag('event', 'conversion', {
            event_category: 'conversions',
            event_label: conversionType,
            value: conversionValue,
            currency: 'USD', // Добавьте валюту если нужно
            // Email данные
            email_id: emailData.email_id,
            lead_id: emailData.lead_id,
            // UTM данные
            campaign_source: utmData.utm_source,
            campaign_medium: utmData.utm_medium,
            campaign_name: utmData.utm_campaign,
            // Дополнительные параметры
            conversion_type: conversionType,
            conversion_source: emailData.email_id ? 'email' : 'organic'
        });

        // Специфичное событие для email конверсий
        if (emailData.email_id) {
            window.gtag('event', 'email_conversion', {
                event_category: 'email_marketing',
                event_label: `${conversionType}_from_email`,
                value: conversionValue,
                email_id: emailData.email_id,
                lead_id: emailData.lead_id,
                conversion_type: conversionType
            });
        }
    }

    // Отправляем в backend
    sendConversionToBackend(emailData.email_id, conversionType, conversionValue);
};

// Отправляем конверсию в backend
const sendConversionToBackend = async (emailId, conversionType, conversionValue) => {
    if (!emailId) return;

    try {
        await fetch('/api/devtools/email-tracking/', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                email_id: emailId,
                event_type: 'conversion',
                conversion_type: conversionType,
                conversion_value: conversionValue,
                timestamp: new Date().toISOString()
            })
        });
    } catch (error) {
        console.error('Failed to track email conversion:', error);
    }
};

export const getCurrentEmailUserData = () => {
    const emailData = JSON.parse(localStorage.getItem('email_tracking') || '{}');
    const utmData = JSON.parse(localStorage.getItem('utm_data') || '{}');

    return {
        emailId: emailData.email_id,
        leadId: emailData.lead_id,
        userId: emailData.user_id,
        campaign: utmData.utm_campaign,
        source: utmData.utm_source,
        isEmailUser: !!emailData.email_id
    };
};

// Функция для добавления tracking pixel (1x1 прозрачный gif) для отслеживания открытий
export const addEmailOpenTracking = (emailId) => {
    if (!emailId) return '';

    return `<img src="/api/devtools/email-tracking/?email_id=${emailId}" width="1" height="1" style="display:none;" alt="" />`;
};

export default AnalyticsTracker;
